import { apiClient, ApiResponse } from './api'

// FAQ-related types
export interface FaqIcon {
  file: string
}

export interface FaqTopic {
  id: number
  title: string
  slug: string
  description?: string
  order?: number
  parent?: number | null
  icon?: FaqIcon
}

export interface Faq {
  id: number
  title?: string // Keep for backwards compat
  question: string
  answer: string
  blurb?: string
  slug: string
  content?: string // Keep for backwards compat
  topic?: FaqTopic
  order?: number
  keywords?: string
  meta_description?: string
  created_at?: string
  updated_at?: string
}

export interface FaqSearchResult {
  faqs: Faq[]
  total: number
  query: string
}

// FAQ and content service functions
export const faqService = {
  // Get FAQ topics
  async getFaqTopics(): Promise<FaqTopic[]> {
    const response = await apiClient.get<FaqTopic[]>('/faq_topics/')
    return response.data
  },

  // Get specific FAQ by slug
  async getFaq(slug: string): Promise<Faq> {
    const response = await apiClient.get<Faq>(`/faq/${slug}/`)
    return response.data
  },

  // Search FAQs
  async searchFaqs(query: Record<string, any>): Promise<FaqSearchResult> {
    const queryStrings: string[] = []
    for (const key in query) {
      queryStrings.push(`${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
    }

    const url = `/faqs/search/?${queryStrings.join('&')}`
    const response = await apiClient.get(url)
    const data = response.data
    // API may return raw array or { faqs, total, query } or { results }
    if (Array.isArray(data)) {
      return { faqs: data, total: data.length, query: query.q || '' }
    }
    if (Array.isArray(data?.results)) {
      return { faqs: data.results, total: data.results.length, query: query.q || '' }
    }
    return data
  },

  // Get FAQs by topic
  async getFaqsByTopic(topicId: number): Promise<Faq[]> {
    const response = await apiClient.get<Faq[]>('/faqs/', {
      params: {
        topics: topicId,
      },
    })
    return response.data
  },

  // Get all FAQs with optional filters
  async getAllFaqs(params?: { topic?: number; search?: string; limit?: number; offset?: number }): Promise<Faq[]> {
    const response = await apiClient.get<Faq[]>('/faqs/', {
      params,
    })
    return response.data
  },

  // Search FAQs with text query
  async searchFaqsText(
    searchText: string,
    options?: {
      topic?: number
      limit?: number
    }
  ): Promise<FaqSearchResult> {
    return this.searchFaqs({
      q: searchText,
      ...options,
    })
  },
}
